import { useAuthStore } from '@/stores/auth.store';
import { queryClient } from './query-client';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3006/api';

/**
 * TokenManager - Singleton điều phối việc làm mới JWT token (Token Refresh Mutex)
 * 
 * Đảm bảo:
 * 1. Chỉ có duy nhất một request POST /api/auth/refresh-token diễn ra tại một thời điểm
 *    ngay cả khi cả Axios và tRPC cùng lúc nhận HTTP 401.
 * 2. Khi token làm mới thành công, tất cả các request đang chờ đều nhận được token mới.
 * 3. Khi token hết hạn hoặc bị từ chối, toàn bộ store được xóa sạch, hủy query cache và
 *    chuyển hướng về /login với thông báo rõ ràng, dứt điểm lỗi Zombie Session.
 */
class TokenManager {
  private refreshPromise: Promise<string | null> | null = null;

  public async refreshAccessToken(): Promise<string | null> {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this.executeRefresh();

    try {
      return await this.refreshPromise;
    } finally {
      this.refreshPromise = null;
    }
  }

  private async executeRefresh(): Promise<string | null> {
    const currentRefreshToken = useAuthStore.getState().refreshToken;

    if (!currentRefreshToken) {
      this.handleAuthFailure('Phiên đăng nhập đã hết hạn, vui lòng đăng nhập lại');
      return null;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/auth/refresh-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: currentRefreshToken }),
      });

      const data = await response.json().catch(() => null);

      if (response.ok && data?.success && data?.data?.token) {
        const newToken: string = data.data.token;
        const newRefreshToken: string = data.data.refreshToken || currentRefreshToken;

        useAuthStore.getState().setTokens({
          accessToken: newToken,
          refreshToken: newRefreshToken,
        });

        return newToken;
      }

      // Server trả về lỗi (401/403/invalid session)
      const message =
        data?.message || 'Phiên đăng nhập không hợp lệ hoặc đã hết hạn';
      this.handleAuthFailure(message);
      return null;
    } catch (err: unknown) {
      // Trường hợp mất mạng (offline)
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        console.warn('[TokenManager] Network offline during token refresh');
        return null;
      }

      this.handleAuthFailure('Không thể kết nối đến máy chủ xác thực');
      return null;
    }
  }

  public handleAuthFailure(message?: string): void {
    if (message) {
      try {
        sessionStorage.setItem('auth_error', message);
      } catch {
        // Bỏ qua lỗi sessionStorage nếu bị chặn
      }
    }

    // Xóa sạch cache queries và logout khỏi Zustand store
    queryClient.clear();
    useAuthStore.getState().logout();

    if (typeof window !== 'undefined') {
      const path = window.location.pathname;
      const isLanding = path === '/landingpage' || path.startsWith('/landingpage/');
      const isLogin = path === '/login' || path.startsWith('/login');

      if (!isLanding && !isLogin) {
        window.location.href = '/login';
      }
    }
  }
}

export const tokenManager = new TokenManager();
